<script lang="ts">
	import { Section, SectionHeader, type SectionTheme } from "#lib/web-ui/primitives/index.js";
	import { InvestmentManagementForm } from "#lib/web-ui/forms/index.js";
	import ExternalLinkIcon from "@lucide/svelte/icons/external-link";
	import { cloudinaryUrl } from "#lib/shared/cloudinary.js";

	let {
		theme = "light" as SectionTheme,
		id,
		label = "Connect With Us",
		title = "Ready to Invest With Confidence?",
		subtitle = "Schedule a time to talk with us about our investment opportunities and learn how they may help you achieve your financial goals.",
		profile = {
			name: "Brian H. Ferguson",
			title: "Founder & Managing Partner",
			bio: "Since his start in real estate, Brian has been passionate about expanding his knowledge and sharing it with others to help them achieve true financial freedom. With 16 years of real estate investing experience, Brian leads our investment strategy with a focus on creating long-term value.",
			imageSrc: cloudinaryUrl("fergmarcapital-com/brian-ferguson-photo", {
				width: 240,
				height: 240,
				crop: "fill"
			}),
			imageAlt: "Brian H. Ferguson"
		},
		calendlyUrl = "https://calendly.com/brianh-ferguson/invest-some-time-with-brian",
		formTitle = "Get in Touch",
		formSubtitle = "Prefer to send a message first? Our team is ready to answer your questions about our investment opportunities."
	}: {
		theme?: SectionTheme;
		id?: string;
		label?: string;
		title?: string;
		subtitle?: string;
		profile?: {
			name: string;
			title: string;
			bio: string;
			imageSrc: string;
			imageAlt: string;
		};
		calendlyUrl?: string;
		formTitle?: string;
		formSubtitle?: string;
	} = $props();
</script>

<Section {theme} {id}>
	<div class="grid grid-cols-1 gap-y-12 lg:grid-cols-2 lg:gap-x-12">
		<!-- Left column - Profile info -->
		<div class="flex h-full flex-col">
			<SectionHeader {label} {title} {subtitle} align="left" class="mb-12" />

			<div class="muted bg-muted flex grow flex-col gap-6 rounded border p-6">
				<div>
					<div class="flex gap-6">
						<img
							src={profile.imageSrc}
							alt={profile.imageAlt}
							class="h-[120px] w-[120px] shrink-0 rounded-full object-cover"
							width="120"
							height="120"
						/>
						<div class="flex flex-col justify-center">
							<h3 class="text-foreground text-xl font-semibold">
								{profile.name}
							</h3>
							<p class="text-foreground/70 text-sm font-medium">{profile.title}</p>
						</div>
					</div>

					<p class="text-muted-foreground mt-6 text-pretty">
						{profile.bio}
					</p>
				</div>

				<div class="mt-auto">
					<p class="text-muted-foreground mb-3 text-sm font-medium">
						The fastest way to connect with {profile.name.split(" ")[0]} is to schedule directly
						on his calendar:
					</p>
					<a
						href={calendlyUrl}
						target="_blank"
						class="bg-primary text-primary-foreground hover:bg-primary/80 flex w-full items-center justify-center gap-2 rounded p-2 font-semibold"
					>
						<span>Schedule a Call</span>
						<ExternalLinkIcon class="h-4 w-4" />
					</a>
				</div>
			</div>
		</div>

		<!-- Right column - Contact Form -->
		<div class="flex flex-col">
			<div class="flex flex-col rounded border p-8 shadow-sm">
				<h3 class="text-foreground text-xl font-semibold">{formTitle}</h3>
				<p class="text-muted-foreground mt-2 mb-4">
					{formSubtitle}
				</p>
				<InvestmentManagementForm />
			</div>
		</div>
	</div>
</Section>
